"use client";

import type { ReactNode } from "react";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { profileHref } from "@/lib/profile-links";

type ProfileLinkProps = {
  /**
   * Username / handle of the profile. When absent (no resolvable handle) the
   * children render non-clickable instead of linking to `/profile/<userId>`,
   * which 404s — see profileHref / getPublicProfile.
   */
  handle: string | null | undefined;
  children?: ReactNode;
  className?: string;
  onClick?: () => void;
  /** Tag used for the non-clickable fallback. Defaults to "span". */
  fallbackAs?: "span" | "div";
};

/**
 * Links to a public profile, or renders its children non-clickable when there is
 * no resolvable handle. Use this everywhere a profile is linked so the
 * no-handle-means-no-link rule lives in one place.
 */
export function ProfileLink({
  handle,
  children,
  className,
  onClick,
  fallbackAs: Fallback = "span",
}: ProfileLinkProps) {
  const href = profileHref(handle);

  if (!href) {
    return <Fallback className={className}>{children}</Fallback>;
  }

  return (
    <BackAwareLink href={href} className={className} onClick={onClick}>
      {children}
    </BackAwareLink>
  );
}
